import React, { forwardRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { plusStatus, startDisclosure, readPlus, PLAN_PRICE } from '../utils/plusStatus';

// ── SoKo Plus card (Alerts page) ──────────────────────────────────────────
//
// One card per selected device. The owner starts Plus here (Stripe Checkout)
// or opens Stripe's billing page (card, plan, cancel). Shared users only see
// the status. The state comes from devices/{id}.plus, which only the cloud
// writes (Stripe webhook / admin comp), so nothing here grants Plus itself.
//
// `returning` is set when Stripe has just sent them back from Checkout:
//   'waiting'   — paid, webhook not seen yet
//   'done'      — Plus is on
//   'slow'      — still not seen after ~30 s
//   'cancelled' — they backed out of Checkout

function IconPlus() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.4-3 8.3-7 9.5C8 19.3 5 15.4 5 11V6l7-3z" />
      <path d="M9.2 12l2 2 3.6-4" />
    </svg>
  );
}

const PlusCard = forwardRef(function PlusCard({ deviceId, plus, isOwner, returning, onNotNow }, ref) {
  const [plan, setPlan] = useState('yearly');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const s = plusStatus(plus, { isOwner });
  const v = readPlus(plus);

  async function go(callable, payload) {
    setBusy(true);
    setErr('');
    try {
      const res = await httpsCallable(functions, callable)(payload);
      const url = res && res.data && res.data.url;
      if (!url) throw new Error('no url');
      window.location.assign(url);
    } catch (e) {
      // HttpsError messages from our callables are written for people.
      const msg = e && e.code && e.code !== 'functions/internal' && e.message ? e.message : '';
      setErr(msg || "Couldn't open that page. Please try again.");
      setBusy(false);
    }
  }

  const startPlus = () => go('createPlusCheckout', { deviceId, plan });
  const openBilling = () => go('createBillingPortal', { deviceId });

  return (
    <section ref={ref} className={`plus-card plus-card--${s.kind}`} aria-label="SoKo Plus">
      <div className="plus-card__head">
        <span className="plus-card__icon"><IconPlus /></span>
        <div className="plus-card__main">
          <div className="plus-card__title">{s.title}</div>
          <div className="plus-card__line">{s.line}</div>
        </div>
      </div>

      {returning === 'waiting' && s.kind === 'none' && (
        <div className="plus-card__note" role="status">Starting SoKo Plus…</div>
      )}
      {returning === 'slow' && s.kind === 'none' && (
        <div className="plus-card__note" role="status">
          SoKo Plus is being set up. It can take a minute; check back shortly.
        </div>
      )}
      {returning === 'done' && s.kind !== 'none' && (
        <div className="plus-card__note plus-card__note--ok" role="status">SoKo Plus is on ✓</div>
      )}
      {returning === 'cancelled' && s.kind === 'none' && (
        <div className="plus-card__note" role="status">Checkout cancelled. Nothing was charged.</div>
      )}

      {s.action === 'start' && (
        <>
          <ul className="plus-card__list">
            <li>Usage and leak alerts</li>
            <li>Slow-leak watch and away mode</li>
            <li>Share with family or a caretaker</li>
            <li>Monthly usage summary</li>
          </ul>
          <div className="plus-card__plans" role="radiogroup" aria-label="Plan">
            {['yearly', 'monthly'].map((p) => (
              <button key={p} type="button" role="radio" aria-checked={plan === p}
                className={`plus-plan ${plan === p ? 'is-on' : ''}`}
                onClick={() => setPlan(p)}>
                <span className="plus-plan__name">{p === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                <span className="plus-plan__price">{PLAN_PRICE[p]}</span>
                {p === 'yearly' && <span className="plus-plan__tag">Save $10</span>}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-primary plus-card__btn" disabled={busy} onClick={startPlus}>
            {busy ? 'Opening checkout…' : (s.button || 'Start SoKo Plus')}
          </button>
          {onNotNow && (
            <button type="button" className="btn btn-secondary plus-card__btn plus-card__later" onClick={onNotNow}>
              Not now
            </button>
          )}
          <p className="plus-card__fine">{startDisclosure({ includedYearUsed: v.includedYearUsed, plan })}</p>
        </>
      )}

      {(s.action === 'manage' || s.action === 'update-card') && (
        <button type="button"
          className={`btn ${s.action === 'update-card' ? 'btn-primary' : 'btn-secondary'} plus-card__btn`}
          disabled={busy} onClick={openBilling}>
          {busy ? 'Opening…' : (s.action === 'update-card' ? 'Update card' : 'Manage billing')}
        </button>
      )}

      {err && <div className="plus-card__err" role="alert">{err}</div>}
    </section>
  );
});

export default PlusCard;
